import { create } from 'zustand';
import { servicesContainer } from '@/core/di/services-container';
import {
  Asset,
  AssetWithVersions,
  AssetApplicationType,
  AssetType,
  AssetUploadFormData,
  DEFAULT_ASSETS_PAGE_SIZE,
} from '../types/asset-types';

interface AssetManagementStore {
  // Data
  assets: Asset[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  selectedAsset: AssetWithVersions | null;

  // Filters
  applicationTypeFilter: AssetApplicationType | null;
  assetTypeFilter: AssetType | null;
  operatorIdFilter: number | null; // Filter by operator (for distributors)
  machineIdFilter: number | null; // Filter by machine ID
  searchQuery: string;

  // UI States
  isLoading: boolean;
  isLoadingDetail: boolean;
  isUploading: boolean;
  isDeleting: boolean;
  isRollingBack: boolean;
  error: string | null;
  uploadError: string | null;

  // Dialog state
  uploadDialogOpen: boolean;
  detailDialogOpen: boolean;
  deleteDialogOpen: boolean;
  assetToDelete: Asset | null;

  // Actions
  fetchAssets: () => Promise<void>;
  fetchAssetDetail: (assetId: number) => Promise<void>;
  uploadAsset: (request: AssetUploadFormData) => Promise<Asset | null>;
  deleteAsset: (assetId: number) => Promise<boolean>;
  rollbackAsset: (assetId: number, versionId: number) => Promise<boolean>;
  setApplicationTypeFilter: (appType: AssetApplicationType | null) => void;
  setAssetTypeFilter: (assetType: AssetType | null) => void;
  setOperatorIdFilter: (operatorId: number | null) => void;
  setMachineIdFilter: (machineId: number | null) => void;
  setSearchQuery: (query: string) => void;
  setCurrentPage: (page: number) => void;
  openUploadDialog: () => void;
  closeUploadDialog: () => void;
  openDetailDialog: (assetId: number) => Promise<void>;
  closeDetailDialog: () => void;
  openDeleteDialog: (asset: Asset) => void;
  closeDeleteDialog: () => void;
  clearUploadError: () => void;
  reset: () => void;
}

export const useAssetManagementStore = create<AssetManagementStore>((set, get) => ({
  // Initial state
  assets: [],
  totalCount: 0,
  currentPage: 0,
  pageSize: DEFAULT_ASSETS_PAGE_SIZE,
  selectedAsset: null,
  applicationTypeFilter: null,
  assetTypeFilter: null,
  operatorIdFilter: null,
  machineIdFilter: null,
  searchQuery: '',
  isLoading: false,
  isLoadingDetail: false,
  isUploading: false,
  isDeleting: false,
  isRollingBack: false,
  error: null,
  uploadError: null,
  uploadDialogOpen: false,
  detailDialogOpen: false,
  deleteDialogOpen: false,
  assetToDelete: null,

  // Actions
  fetchAssets: async () => {
    const {
      applicationTypeFilter,
      assetTypeFilter,
      operatorIdFilter,
      machineIdFilter,
      searchQuery,
      currentPage,
      pageSize,
    } = get();

    set({ isLoading: true, error: null });
    try {
      const services = servicesContainer.getServices();
      const response = await services.getAssetsUseCase.execute({
        applicationType: applicationTypeFilter || undefined,
        assetType: assetTypeFilter || undefined,
        operatorId: operatorIdFilter || undefined,
        machineId: machineIdFilter || undefined,
        search: searchQuery || undefined,
        skip: currentPage * pageSize,
        take: pageSize,
      });

      if (response.isSuccess) {
        set({
          assets: response.items ?? [],
          totalCount: response.totalCount,
          isLoading: false,
        });
      } else {
        set({
          error: response.errorMessage || 'Failed to load assets',
          isLoading: false,
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load assets',
        isLoading: false,
      });
    }
  },

  fetchAssetDetail: async (assetId: number) => {
    set({ isLoadingDetail: true, error: null });
    try {
      const services = servicesContainer.getServices();
      const response = await services.getAssetDetailUseCase.execute(assetId);

      if (response.isSuccess) {
        set({
          selectedAsset: {
            asset: response.asset,
            versions: response.versions,
          },
          isLoadingDetail: false,
        });
      } else {
        set({
          error: response.errorMessage || 'Failed to load asset details',
          isLoadingDetail: false,
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load asset details',
        isLoadingDetail: false,
      });
    }
  },

  uploadAsset: async (request: AssetUploadFormData) => {
    set({ isUploading: true, uploadError: null });
    try {
      const services = servicesContainer.getServices();
      const response = await services.uploadAssetUseCase.execute(request);

      if (response.isSuccess && response.asset) {
        // Refresh the list
        await get().fetchAssets();
        set({ isUploading: false, uploadDialogOpen: false });
        return response.asset;
      } else {
        set({
          uploadError: response.errorMessage || 'Failed to upload asset',
          isUploading: false,
        });
        return null;
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to upload asset';
      set({ uploadError: message, isUploading: false });
      return null;
    }
  },

  deleteAsset: async (assetId: number) => {
    set({ isDeleting: true });
    try {
      const services = servicesContainer.getServices();
      const response = await services.deleteAssetUseCase.execute(assetId);

      if (response.isSuccess) {
        // Refresh the list
        await get().fetchAssets();
        set({
          isDeleting: false,
          deleteDialogOpen: false,
          assetToDelete: null,
        });
        return true;
      } else {
        set({
          isDeleting: false,
          error: response.errorMessage || 'Failed to delete asset',
        });
        return false;
      }
    } catch (error) {
      set({
        isDeleting: false,
        error: error instanceof Error ? error.message : 'Failed to delete asset',
      });
      return false;
    }
  },

  rollbackAsset: async (assetId: number, versionId: number) => {
    set({ isRollingBack: true });
    try {
      const services = servicesContainer.getServices();
      const response = await services.rollbackAssetUseCase.execute(assetId, versionId);

      if (response.isSuccess) {
        // Refresh both list and detail
        await get().fetchAssets();
        await get().fetchAssetDetail(assetId);
        set({ isRollingBack: false });
        return true;
      } else {
        set({
          isRollingBack: false,
          error: response.errorMessage || 'Failed to rollback asset',
        });
        return false;
      }
    } catch (error) {
      set({
        isRollingBack: false,
        error: error instanceof Error ? error.message : 'Failed to rollback asset',
      });
      return false;
    }
  },

  setApplicationTypeFilter: (appType: AssetApplicationType | null) => {
    set({
      applicationTypeFilter: appType,
      currentPage: 0,
    });
  },

  setAssetTypeFilter: (assetType: AssetType | null) => {
    set({
      assetTypeFilter: assetType,
      currentPage: 0,
    });
  },

  setOperatorIdFilter: (operatorId: number | null) => {
    set({
      operatorIdFilter: operatorId,
      currentPage: 0,
    });
  },

  setMachineIdFilter: (machineId: number | null) => {
    set({
      machineIdFilter: machineId,
      currentPage: 0,
    });
  },

  setSearchQuery: (query: string) => {
    set({
      searchQuery: query,
      currentPage: 0,
    });
  },

  setCurrentPage: (page: number) => {
    set({ currentPage: page });
  },

  openUploadDialog: () => {
    set({ uploadDialogOpen: true, uploadError: null });
  },

  closeUploadDialog: () => {
    set({ uploadDialogOpen: false, uploadError: null });
  },

  openDetailDialog: async (assetId: number) => {
    set({ detailDialogOpen: true, selectedAsset: null });
    await get().fetchAssetDetail(assetId);
  },

  closeDetailDialog: () => {
    set({
      detailDialogOpen: false,
      selectedAsset: null,
    });
  },

  openDeleteDialog: (asset: Asset) => {
    set({
      deleteDialogOpen: true,
      assetToDelete: asset,
    });
  },

  closeDeleteDialog: () => {
    set({
      deleteDialogOpen: false,
      assetToDelete: null,
    });
  },

  clearUploadError: () => {
    set({ uploadError: null });
  },

  reset: () => {
    set({
      assets: [],
      totalCount: 0,
      currentPage: 0,
      pageSize: DEFAULT_ASSETS_PAGE_SIZE,
      selectedAsset: null,
      applicationTypeFilter: null,
      assetTypeFilter: null,
      operatorIdFilter: null,
      machineIdFilter: null,
      searchQuery: '',
      isLoading: false,
      isLoadingDetail: false,
      isUploading: false,
      isDeleting: false,
      isRollingBack: false,
      error: null,
      uploadError: null,
      uploadDialogOpen: false,
      detailDialogOpen: false,
      deleteDialogOpen: false,
      assetToDelete: null,
    });
  },
}));
